// Per-merchant brand theme application (custom overlay).
//
// A brand theme recolors the workspace accent palette. It is applied by adding
// a `theme-<id>` class on <body>; the corresponding CSS variable overrides live
// in dashboard/assets/scss/_brand-themes.scss. The `default` theme uses no
// class and keeps upstream colors. This is orthogonal to the light/dark mode
// (the `.dark` body class, handled by themeHelper.js).

export const BRAND_THEMES = ['default', 'green'];
export const BRAND_THEME_DEFAULT_ACTIVE = 'green';
export const BRAND_THEME_DEFAULT_ENABLED = ['green'];
export const BRAND_THEME_LEGACY_ACTIVE = 'default';
export const BRAND_THEME_LEGACY_ENABLED = ['default'];

export const WIDGET_COLOR_BY_THEME = {
  default: '#1f93ff',
  green: '#25D366',
};

// Customer message text color for new web widgets (product default).
export const DEFAULT_WIDGET_FOREGROUND_COLOR = '#000000';

export const PLATFORM_DEFAULT_WIDGET_COLORS = [
  '#1f93ff',
  '#009ce0',
  '#2781f6',
];

const THEME_CLASS_PREFIX = 'theme-';

const resolveEnabledThemes = settings => {
  const list = settings.brand_themes_enabled;
  if (Array.isArray(list) && list.length) {
    return list.filter(id => BRAND_THEMES.includes(id));
  }
  const theme = settings.brand_theme;
  if (theme && BRAND_THEMES.includes(theme)) {
    return [theme];
  }
  // Accounts created before brand-theme defaults: implicit blue theme.
  return BRAND_THEME_LEGACY_ENABLED;
};

// Resolve the active theme for an account, honoring the super-admin allow-list.
export const resolveBrandTheme = (account = {}) => {
  const settings = account?.settings || {};
  const enabled = resolveEnabledThemes(settings);
  const fallbackActive = enabled.includes(BRAND_THEME_DEFAULT_ACTIVE)
    ? BRAND_THEME_DEFAULT_ACTIVE
    : enabled[0] || BRAND_THEME_LEGACY_ACTIVE;
  const hasAllowList = Object.prototype.hasOwnProperty.call(
    settings,
    'brand_themes_enabled'
  );
  const active =
    settings.brand_theme ||
    (hasAllowList ? fallbackActive : BRAND_THEME_DEFAULT_ACTIVE);

  if (enabled.includes(active)) {
    return active;
  }

  // Honor explicit brand_theme even when allow-list is still legacy/default-only.
  if (settings.brand_theme && BRAND_THEMES.includes(settings.brand_theme)) {
    return settings.brand_theme;
  }

  if (settings.registration_status === 'pending_approval') {
    return BRAND_THEME_DEFAULT_ACTIVE;
  }

  return fallbackActive;
};

export const applyBrandTheme = themeId => {
  const { body } = document;
  if (!body) return;
  BRAND_THEMES.forEach(id => {
    if (id !== 'default') {
      body.classList.remove(`${THEME_CLASS_PREFIX}${id}`);
    }
  });
  if (themeId && themeId !== 'default' && BRAND_THEMES.includes(themeId)) {
    body.classList.add(`${THEME_CLASS_PREFIX}${themeId}`);
  }
};

export const setBrandThemeFromAccount = account =>
  applyBrandTheme(resolveBrandTheme(account));

export const isPlatformDefaultWidgetColor = color => {
  const normalized = (color || '').toLowerCase();
  return (
    !normalized ||
    PLATFORM_DEFAULT_WIDGET_COLORS.some(c => c.toLowerCase() === normalized)
  );
};

export const defaultWidgetColorForAccount = (account = {}) => {
  const theme = resolveBrandTheme(account);
  return WIDGET_COLOR_BY_THEME[theme] || WIDGET_COLOR_BY_THEME.default;
};

export const resolveWidgetColorForAccount = (account = {}, widgetColor) => {
  if (isPlatformDefaultWidgetColor(widgetColor)) {
    return defaultWidgetColorForAccount(account);
  }
  return widgetColor;
};

const FALLBACK_CHART_BAR_COLOR = 'rgb(31, 147, 255)';

// Chart.js bar fill — reads themed --blue-9 from <body> (see _brand-themes.scss).
export const brandThemeChartBarColor = () => {
  if (typeof document === 'undefined') return FALLBACK_CHART_BAR_COLOR;

  const channels = getComputedStyle(document.body)
    .getPropertyValue('--blue-9')
    .trim();
  const parts = channels.split(/\s+/).filter(Boolean);
  if (parts.length !== 3) return FALLBACK_CHART_BAR_COLOR;

  return `rgb(${parts.join(', ')})`;
};
